import { FC, useEffect, useState, MouseEvent } from "react";
import { styled } from "@mui/material/styles";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    Box,
    CircularProgress,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import { AdminLayout } from "../../../../components/layouts/admin-layout/admin-layout";
import { CustomButton } from "../../../../components";
import { Colors } from "../../../../configs";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { useSnackbarContext } from "../../../../providers/SnackbarProvider";
import {
    deletePriceOverrideConfigAction,
    getAllPriceOverrideConfigsAction,
} from "../../../../redux/actions/priceOverrideConfigAction";
import { getCategoryAction } from "../../../../redux/actions/pricingAction";
import { AddPriceOverrideDrawer } from "../../../../components/admin/add-price-override-drawer/AddPriceOverrideDrawer";
import { IPriceOverrideConfigResponse } from "../../../../typings/interfaces/responses/priceOverrideConfigResponse";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        fontWeight: "bold",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

export const PriceOverridesPage: FC = () => {
    const dispatch = useAppDispatch();
    const snackbar = useSnackbarContext();
    const { configs, configsLoading, addConfigLoading, updateConfigLoading } =
        useAppSelector((state) => state.priceOverrideConfig);
    const { categoryResponse, categoryResponseLoading } = useAppSelector((state) => state.pricing);

    // Filter categories similar to POS Check-in page
    const categoryiesList = categoryResponse?.body?.filter(tab => tab.showInMenu) || [];
    const alterationCategory = { id: -100000, categoryName: "Alteration" };
    const displayCategories = [...categoryiesList, alterationCategory];

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedConfig, setSelectedConfig] = useState<IPriceOverrideConfigResponse | undefined>(
        undefined
    );
    const [openDrawer, setOpenDrawer] = useState(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);

    useEffect(() => {
        dispatch(getAllPriceOverrideConfigsAction());
        dispatch(getCategoryAction());
    }, [dispatch]);

    useEffect(() => {
        if (!categoryResponseLoading && selectedCategoryId === null) {
            if (categoryiesList.length > 0) {
                setSelectedCategoryId(categoryiesList[0].id);
            } else {
                setSelectedCategoryId(-100000); // Default to alteration if no others
            }
        }
    }, [categoryResponseLoading, categoryiesList, selectedCategoryId]);

    const filteredConfigs = configs?.filter(c => Number(c.categoryId) === Number(selectedCategoryId)) || [];

    const handleClick = (
        event: MouseEvent<HTMLButtonElement>,
        config: IPriceOverrideConfigResponse
    ) => {
        setAnchorEl(event.currentTarget);
        setSelectedConfig(config);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDeleteConfig = async () => {
        if (!selectedConfig) {
            return;
        }
        handleClose();

        try {
            await dispatch(deletePriceOverrideConfigAction(selectedConfig.id)).unwrap();
            snackbar.showSnackbar("Config deleted successfully!", "success");
            dispatch(getAllPriceOverrideConfigsAction());
        } catch (error) {
            snackbar.showSnackbar(
                "Failed to delete config. Please try again.",
                "error"
            );
        }
    };

    return (
        <AdminLayout>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "3rem",
                    height: "57.5rem",
                    marginTop: "4.357rem",
                    paddingInline: "5rem",
                    width: "100%",
                }}
            >
                <AddPriceOverrideDrawer
                    open={openDrawer}
                    toggleDrawer={() => setOpenDrawer(false)}
                    populateFor={selectedConfig}
                    selectedCategoryId={selectedCategoryId}
                />

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="h4" sx={{ color: Colors.TEXT_GRAY_DARK }}>
                        Price Overrides
                    </Typography>
                    <CustomButton
                        sx={{ width: "11rem", height: "3rem" }}
                        onClick={() => {
                            setSelectedConfig(undefined);
                            setOpenDrawer(true);
                        }}
                        disabled={configsLoading || addConfigLoading || updateConfigLoading}
                    >
                        {configsLoading || addConfigLoading || updateConfigLoading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            "Add New"
                        )}
                    </CustomButton>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "0.5rem",
                        flexWrap: "wrap",
                        marginBottom: "-1.5rem"
                    }}
                >
                    {displayCategories.map((cat: any) => (
                        <CustomButton
                            key={cat.id}
                            variant={selectedCategoryId === cat.id ? "contained" : "outlined"}
                            onClick={() => setSelectedCategoryId(cat.id)}
                            sx={{
                                background: selectedCategoryId === cat.id ? Colors.PRIMARY : Colors.WHITE,
                                color: selectedCategoryId === cat.id ? Colors.WHITE : Colors.PRIMARY,
                                borderRadius: "0.5rem",
                                textTransform: "none",
                                padding: "0.5rem 1rem",
                                minWidth: "6rem",
                                "&:hover": {
                                    background: selectedCategoryId === cat.id ? Colors.PRIMARY : "#f5f5f5"
                                }
                            }}
                        >
                            {cat.categoryName}
                        </CustomButton>
                    ))}
                </Box>

                <Box
                    sx={{
                        maxHeight: "51rem",
                        width: "100%",
                        overflowY: "auto",
                        "&::-webkit-scrollbar": { width: "0.5rem" },
                        "&::-webkit-scrollbar-thumb": { background: "#888" },
                    }}
                >
                    <Table sx={{ width: "100%" }} aria-label="customized table">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Category</StyledTableCell>
                                <StyledTableCell>Processing Type</StyledTableCell>
                                <StyledTableCell>Slab (Min - Max)</StyledTableCell>
                                <StyledTableCell>Reduce Amount</StyledTableCell>
                                <StyledTableCell>Action</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {configsLoading && (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={4} align="center">
                                        <CircularProgress />
                                    </StyledTableCell>
                                </StyledTableRow>
                            )}
                            {!configsLoading &&
                                filteredConfigs.map((row) => (
                                    <StyledTableRow key={row.id}>
                                        <StyledTableCell>
                                            {row.categoryName || displayCategories.find(c => c.id === Number(row.categoryId))?.categoryName || "Unknown"}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {row.processingType === "PRESS_ONLY" ? "Press Only" : row.processingType === "CLEAN_ONLY" ? "Clean Only" : "Applicable for All"}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            ${row.slabMinPrice.toFixed(2)} - ${row.slabMaxPrice.toFixed(2)}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {row.reduceAmountType === "PERCENTAGE" ? `${row.reduceAmount}%` : `$${row.reduceAmount.toFixed(2)}`}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <IconButton onClick={(e) => handleClick(e, row)}>
                                                <MoreHorizIcon
                                                    sx={{ fontSize: "2rem" }}
                                                    color="primary"
                                                />
                                            </IconButton>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            {(!configsLoading && filteredConfigs.length === 0) && (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={5} align="center">
                                        No configs found for this category
                                    </StyledTableCell>
                                </StyledTableRow>
                            )}
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleClose}
                            >
                                <MenuItem
                                    onClick={() => {
                                        setOpenDrawer(true);
                                        handleClose();
                                    }}
                                >
                                    <ListItemIcon>
                                        <EditIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>Edit Config</ListItemText>
                                </MenuItem>

                                <MenuItem onClick={handleDeleteConfig}>
                                    <ListItemIcon>
                                        <DeleteIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>Delete Config</ListItemText>
                                </MenuItem>
                            </Menu>
                        </TableBody>
                    </Table>
                </Box>
            </Box>
        </AdminLayout>
    );
};
